import { useEffect, useState } from 'react';
import {
  useSession,
  Education,
  JobSection,
  SocialMediaSection,
  DesktopNav,
  Grid,
  NotesSection,
  ReportLoading,
  ReportActionsTopBar,
  ReportNavMobile,
  PhoneSection,
  ReportOptions,
  EmailSection,
  PossibleOwnedAssets,
  AddressHistory,
  ReportFactory,
  ReportRating,
  PersonOverview,
  MaritalRecordsSection,
  RelativesSection,
  AssociatesSection,
  NeighborsSection,
  BankruptcySection,
  CriminalOrTraffic,
  CustomIcons,
  RelatedReportsSection,
  ReportNullState,
  isZeroed,
  useScrollToSectionOnNavigate,
  ReportMonitor,
  useReportMonitors,
  Monitoring,
  FreeBonusData,
  flattenBonusData,
  NamesAncestrySection,
  nameize,
  ReportChangesOverview,
  useReport,
  useRemouladeReportSnapshot,
  ReportClaiming,
  PdfSection,
  DebugMenu,
} from '@ltvco/refresh-lib';
import type { ReportRouteProps } from './types';
import { getPersonNavLinkData } from 'navLinkData/personNavLinkData';
import { constants } from 'appConstants';
import { useLocation } from 'react-router-dom';

interface PersonReportProps extends ReportRouteProps {}

const reportType = 'person';

export function PersonReportPage({
  permalink,
  isMonitored = false,
}: PersonReportProps) {
  const {
    session: { account },
  } = useSession();

  // TODO: all this monitored report logic needs a refactor.
  // There is no point in refetching a report when a user toggles monitoring, so we use the initial value.
  const [initialIsMonitored, setInitialIsMonitored] = useState(isMonitored);
  const coreResult = useReport(permalink, initialIsMonitored);
  const remouladeResult = useRemouladeReportSnapshot(
    permalink,
    initialIsMonitored
  );
  const currentUserInfo = account?.account?.user_info;
  const queryResult = (
    initialIsMonitored ? remouladeResult : coreResult
  ) as typeof coreResult;

  // If remoulade fails, we fall back to core. Most likely cause is race condition between creating monitor
  // and remoulade fetching data from core.
  useEffect(() => {
    if (remouladeResult.isError) {
      setInitialIsMonitored(false);
    }
  }, [remouladeResult.isError]);

  const zeroed = isZeroed(queryResult);
  const { hash } = useLocation();
  useScrollToSectionOnNavigate(hash, queryResult);

  const [showCriminalData, setShowCriminalData] = useState<boolean>(false);
  const [showBankruptcyData, setShowBankruptcyData] = useState<boolean>(false);

  const [reportOptions, setReportOptions] = useState<ReportOptions>({
    potentialOwnerIndex: 0,
    showHighConfidenceDataOnly: true,
    higherConfidenceThreshold: constants.config.higherConfidenceThreshold,
  });
  const handleSetReportOptions = (newOptions: Partial<ReportOptions>) => {
    setReportOptions({ ...reportOptions, ...newOptions });
  };

  const { getMonitors } = useReportMonitors();
  const [activeMonitor, setActiveMonitor] = useState(false);
  const [activeClaiming, setActiveClaiming] = useState(false);
  const [disableClaiming, setDisableClaiming] = useState(false);

  useEffect(() => {
    setActiveMonitor(
      getMonitors.data?.some(
        (monitor: ReportMonitor) => monitor.permalink === permalink
      )
    );
  }, [getMonitors.data, permalink]);

  useEffect(() => {
    setDisableClaiming(
      getMonitors.data?.some((monitor: ReportMonitor) => {
        return (
          monitor.report_type === 'detailed_person_report' &&
          monitor.is_claimed &&
          permalink !== monitor.permalink
        );
      })
    );
    setActiveClaiming(
      getMonitors.data?.some((monitor: ReportMonitor) => {
        return monitor.permalink === permalink && monitor.is_claimed;
      })
    );
  }, [getMonitors.data, permalink]);

  const hideCriminalBJLData = account?.account.user_settings
    .hide_criminal_records as boolean;

  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={14} />;
  }

  if (zeroed) {
    return <ReportNullState />;
  }

  const report = ReportFactory.create(queryResult?.data, 'person');
  const reportUpgraded = queryResult.data.meta.report_upgraded;

  let showReportClaiming = false;
  if (
    currentUserInfo?.first_name &&
    currentUserInfo.last_name &&
    report.personName.includes(currentUserInfo.first_name) &&
    report.personName.includes(currentUserInfo.last_name)
  ) {
    showReportClaiming = true;
  }

  const MonitoringComponent =
    showReportClaiming && !disableClaiming ? (
      <ReportClaiming
        permalink={permalink}
        reportType={reportType}
        activeMonitor={activeClaiming}
      />
    ) : (
      <Monitoring
        activeMonitor={activeMonitor}
        permalink={permalink}
        reportType={reportType}
      />
    );

  if (!report) return <ReportLoading menuItems={14} />;

  if (!report?.data?.people && !report?.data?.people?.length) {
    return <ReportNullState />;
  }

  const owner = report.getOwner(reportOptions);
  const {
    ownerName,
    nameInfo,
    emails,
    phones,
    educations,
    jobs,
    profiles,
    usernames,
    addresses,
    ownedAssets,
    marital,
    relatives,
    associates,
    neighbors,
    courts,
    bonusData,
  } = owner;
  const flattenedBonusData = flattenBonusData(bonusData);

  const personNavLinkData = getPersonNavLinkData(
    owner,
    showCriminalData,
    showBankruptcyData,
    hideCriminalBJLData,
    reportUpgraded,
    flattenedBonusData.length
  );

  document.title = `${report.personName} - NumberGuru`;
  const dob = owner.identity?.dobs?.[0]?.date?.parsed?.year?.toString() ?? '';

  return (
    <>
      <Grid container direction={'row'} columns={12} spacing={7}>
        <Grid
          item
          sm={12}
          md={4}
          lg={4}
          sx={{ display: { xs: 'none', sm: 'none', md: 'block', lg: 'block' } }}
        >
          <DesktopNav
            navLinkData={personNavLinkData}
            reportDataPoint={report.personName}
            permalink={permalink}
          />
        </Grid>
        <Grid item sm={12} md={8} lg={8} marginTop={3}>
          {activeMonitor && (
            <ReportChangesOverview
              permalink={permalink}
              navLinkData={personNavLinkData}
            />
          )}
          <ReportActionsTopBar
            reportPermaLink={permalink}
            reportTitle="Person"
            reportType={reportType}
            reportMonitorState={activeMonitor}
            deleteCurrentReport={(permalink: string) => permalink}
            reportUpgraded={reportUpgraded}
          />
          <PersonOverview
            reportType={reportType}
            owner={owner}
            showHighConfidenceToggle
            showHighConfidenceDataOnly={
              reportOptions.showHighConfidenceDataOnly
            }
            setShowHighConfidenceDataOnly={(value: boolean) => {
              handleSetReportOptions({
                showHighConfidenceDataOnly: value,
              });
            }}
            personName={ownerName}
            personOverviewItemsData={{
              hideCriminalBJLData,
              phones,
              emails,
              addresses,
              relatives,
              images: owner.images,
              profiles,
              userIds: owner.userIds,
              usernames,
              permalink,
            }}
            Monitoring={MonitoringComponent}
          />
          <PdfSection permalink={permalink} />
          <PhoneSection
            phonesCount={phones.length}
            personName={ownerName}
            phoneList={phones}
            permalink={permalink}
          />
          <EmailSection
            emailsCount={emails.length}
            personName={ownerName}
            emailList={emails}
            permalink={permalink}
          />
          <AddressHistory
            addresses={addresses}
            addressCount={addresses.length}
            personName={ownerName}
            permalink={permalink}
          />

          <RelativesSection
            relativesCount={relatives?.length || 0}
            personName={ownerName}
            relatives={relatives || []}
            permalink={permalink}
          />
          <MaritalRecordsSection
            personName={ownerName}
            marital={marital}
            permalink={permalink}
          />
          <NamesAncestrySection
            firstName={nameize(nameInfo?.parsed?.first ?? null)}
            surName={nameize(nameInfo?.parsed?.last ?? null)}
            yearOfBirth={dob}
          />
          <AssociatesSection
            associatesCount={associates?.length || 0}
            personName={ownerName}
            associates={associates || []}
            permalink={permalink}
          />
          <NeighborsSection
            neighborsCount={neighbors?.length || 0}
            personName={ownerName}
            neighbors={neighbors || []}
            permalink={permalink}
          />
          {!hideCriminalBJLData && (
            <>
              <CriminalOrTraffic
                personName={ownerName}
                criminalData={courts.criminal || []}
                permalink={permalink}
                icon={
                  <CustomIcons.GavelIcon
                    sx={{ height: '48px', width: '48px' }}
                  />
                }
                showCriminalData={showCriminalData}
                setShowCriminalData={setShowCriminalData}
              />
              <BankruptcySection
                personName={ownerName}
                bankruptcyData={courts.bankruptcy || []}
                permalink={permalink}
                showBankruptcyData={showBankruptcyData}
                setShowBankruptcyData={setShowBankruptcyData}
              />
            </>
          )}
          <JobSection
            personName={ownerName}
            jobs={jobs}
            permalink={permalink}
          />
          <Education
            personName={ownerName}
            educations={educations}
            permalink={permalink}
          />
          <SocialMediaSection
            personName={ownerName}
            profiles={profiles}
            usernames={usernames}
            permalink={permalink}
          />
          <PossibleOwnedAssets
            personName={ownerName}
            ownedAssets={ownedAssets}
            permalink={permalink}
          />
          <FreeBonusData
            personName={ownerName}
            reportUpgraded={reportUpgraded}
            flattenedBonusData={flattenedBonusData}
            owner={report.data.people?.[0]}
          />
          <NotesSection permalink={permalink} />
          <RelatedReportsSection
            reportTitle={ownerName}
            reportType="person"
            reportOwner={ownerName}
            potentialOwners={report.data.people}
            relatives={relatives}
            addresses={addresses}
            phones={phones}
            emails={emails}
            usernames={usernames}
            message={`Looking into people, addresses and contacts associated with ${ownerName} may yield even more insights`}
          />
          <ReportRating
            permalink={permalink}
            rating={report.data.meta?.rating || null}
          />
        </Grid>
      </Grid>
      <ReportNavMobile navLinkData={personNavLinkData} />
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
    </>
  );
}
